import { apiUrl } from 'shared/config/api'

/**
 * API セッション切れの検知。
 *
 * Cognito 時代は 401 を受けたら refresh token でトークンを取り直して再送していたが、
 * API セッション（`api_session`）には refresh の仕組みが無い。失効（idle 30 日・絶対 365 日・
 * パスワード変更による全失効）した後の 401 は、ログインし直す以外に回復手段がない。
 *
 * ただし 401 がすべてセッション切れとは限らない。メールアドレス変更の確認コード不一致
 * （`PUT /v1/tenants/:tenantId/users/:userId` の 401 `email_confirmation_code_invalid`）のように
 * 業務エラーとして 401 を返す API がある。api は「セッション切れの判定は HTTP status 401 で行い、
 * 文言で区別しない」方針のため、401 を受けたら `GET /v1/users/me` で確かめ、そこも 401 のときだけ
 * セッション切れとして通知する。
 */

type Listener = () => void

let listener: Listener | null = null
let probing: Promise<void> | null = null

/**
 * 認証 API 自体の 401 は資格情報不一致などの業務エラーなので、セッション切れの判定に使わない。
 * SAML のセッション削除(`/saml/session`)はログアウト・後始末の途中で呼ぶため同じく除外する。
 */
const isAuthEndpoint = (url: string): boolean => url.includes('/v1/auth/') || url.includes('/saml/session')

/**
 * セッション切れの通知先を登録する。戻り値で解除する。
 * 通知先は 1 つだけ（AuthGate が認証済みの間だけ登録する）。
 */
export const subscribeSessionExpired = (next: Listener): (() => void) => {
  listener = next
  return () => {
    if (listener === next) {
      listener = null
    }
  }
}

const probeSession = async (): Promise<void> => {
  try {
    const res = await fetch(apiUrl('/v1/users/me'), {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    })
    if (res.status === 401) {
      // 通知は 1 回だけにする。後始末(endSession)自身が送る DELETE が 401 を返しても
      // もう一度確認・通知して後始末が二重に走らないよう、呼ぶ前に通知先を外す。
      // 再ログインで認証済みに戻れば AuthGate が改めて登録する。
      const notify = listener
      listener = null
      notify?.()
    }
  } catch {
    // ネットワーク断ではセッション切れと断定できない。何もしない。
  }
}

/**
 * API から 401 を受けたことを知らせる。
 * 通知先が無い（未ログイン・初回判定中）ときや認証 API の 401 は無視する。
 * 同時に複数の 401 が返っても確認は 1 回にまとめる。
 */
export const reportUnauthorized = (url: string | undefined): void => {
  if (listener == null) return
  if (url != null && isAuthEndpoint(url)) return
  probing ??= probeSession().finally(() => {
    probing = null
  })
}
